import { useAuth0 } from '@auth0/auth0-react'
import { Copy, ExternalLink, Users, Video } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { useItemsCalendarioPorDia } from '@/core/hooks/useCalendario'
import { useAhora } from '@/hooks/useAhora'
import { usePermissions } from '@/hooks/usePermissions'
import { abrirEnlace, esEnlaceAbrible } from '@/lib/abrir-enlace'
import { copiarEnlaceMeet } from '@/lib/copiar-enlace'
import { horaVisibleEvento } from '@/lib/fechaEvento'
import { esMeetDelUsuario, FILTROS_SOLO_MEETS, meetInminente } from '@/lib/meets'

import { AvisoCriticoDialog } from './AvisoCriticoDialog'
import { comoEvento, tituloAviso } from './tituloAviso'

import type { ItemCalendario } from '@/core/types/calendario'
import type { UsuarioMeet } from '@/lib/meets'

const claveVistos = (email: string | undefined) => `brainhub-aviso-meet:${email ?? 'anon'}`

/** Día local en `YYYY-MM-DD`, que es como se agrupan los items del calendario. */
const diaLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * Meets de hoy que el usuario ya reconoció.
 *
 * Una sola clave con el día adentro: guardar una por fecha deja basura acumulándose en el
 * navegador, y al cambiar el día el registro viejo se descarta entero.
 */
const leerVistos = (email: string | undefined, hoy: string): string[] => {
  try {
    const crudo = localStorage.getItem(claveVistos(email))
    if (!crudo) return []
    const { dia, vistos } = JSON.parse(crudo) as { dia?: string; vistos?: string[] }
    return dia === hoy && Array.isArray(vistos) ? vistos : []
  } catch {
    // Modo incógnito o storage bloqueado: el aviso vuelve a aparecer, que es el lado seguro.
    return []
  }
}

const guardarVistos = (email: string | undefined, hoy: string, vistos: string[]) => {
  try {
    localStorage.setItem(claveVistos(email), JSON.stringify({ dia: hoy, vistos }))
  } catch {
    /* sin storage no se recuerda el descarte; el aviso igual se cierra en esta sesión */
  }
}

function AvisoMeetHoyInterno({ usuario }: { usuario: UsuarioMeet }) {
  const { user } = useAuth0()
  const ahora = useAhora()
  const hoy = diaLocal(ahora)

  const { data: dias } = useItemsCalendarioPorDia(
    ahora.getFullYear(),
    ahora.getMonth() + 1,
    FILTROS_SOLO_MEETS,
  )

  // El día viaja con los reconocidos: al cruzar la medianoche la lista se vacía sola, sin un
  // efecto que la limpie.
  const [reconocidos, setReconocidos] = useState<{ dia: string; ids: string[] }>(() => ({
    dia: hoy,
    ids: leerVistos(user?.email, hoy),
  }))
  const vistos = useMemo(
    () => (reconocidos.dia === hoy ? reconocidos.ids : []),
    [reconocidos, hoy],
  )

  const pendientes = useMemo<ItemCalendario[]>(() => {
    const yaVistos = new Set(vistos)
    const delDia = dias?.find((d) => d.fecha === hoy)?.items ?? []
    return delDia.filter(
      (item) =>
        esMeetDelUsuario(item, usuario) &&
        meetInminente(comoEvento(item), ahora) &&
        !yaVistos.has(item.id),
    )
  }, [dias, hoy, ahora, vistos, usuario])

  const entendido = () => {
    const nuevos = [...vistos, ...pendientes.map((m) => m.id)]
    setReconocidos({ dia: hoy, ids: nuevos })
    guardarVistos(user?.email, hoy, nuevos)
  }

  if (pendientes.length === 0) return null

  const unico = pendientes.length === 1 ? pendientes[0] : null
  const conEnlace = pendientes.filter((m) => m.meet_url)

  return (
    <AvisoCriticoDialog
      open
      onEntendido={entendido}
      severidad="importante"
      icono={Video}
      titulo={tituloAviso(pendientes, ahora)}
      descripcion={
        unico
          ? 'No vuelve a aparecer hoy después de que la cierres.'
          : 'Revisá los horarios: el aviso no vuelve a aparecer hoy.'
      }
      acciones={
        unico?.meet_url ? (
          <>
            <Button variant="outline" onClick={() => copiarEnlaceMeet(unico.meet_url!)}>
              <Copy className="mr-2 h-4 w-4" />
              Copiar enlace
            </Button>
            {esEnlaceAbrible(unico.meet_url) && (
              <Button variant="outline" onClick={() => abrirEnlace(unico.meet_url!)}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Abrir
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <ul className="space-y-3">
        {pendientes.map((meet) => (
          <li key={meet.id} className="rounded-lg border bg-muted/40 p-3 text-left">
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 flex-1 truncate text-sm font-medium">{meet.titulo}</p>
              <span className="shrink-0 font-mono text-sm font-semibold tabular-nums">
                {horaVisibleEvento(comoEvento(meet)) ?? 'Todo el día'}
              </span>
            </div>

            {meet.participantes && meet.participantes.length > 0 && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {meet.participantes.map((p) => p.split('@')[0]).join(', ')}
                </span>
              </p>
            )}

            {/* Con varias reuniones, cada una necesita su propio botón. */}
            {!unico && meet.meet_url && (
              <div className="mt-2 flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => copiarEnlaceMeet(meet.meet_url!)}
                >
                  <Copy className="mr-1.5 h-3 w-3" />
                  Copiar enlace
                </Button>
                {esEnlaceAbrible(meet.meet_url) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => abrirEnlace(meet.meet_url!)}
                  >
                    <ExternalLink className="mr-1.5 h-3 w-3" />
                    Abrir
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {conEnlace.length === 0 && (
        <p className="mt-3 text-center text-xs text-muted-foreground">
          El enlace de Meet todavía no está disponible.
        </p>
      )}
    </AvisoCriticoDialog>
  )
}

/**
 * Aviso de las reuniones de hoy, imposible de pasar por alto.
 *
 * Avisa solo las reuniones que le corresponden al usuario, según `esMeetDelUsuario`. El permiso
 * se chequea acá afuera para que sin él no se monte el interno: gatearlo solo en el JSX dejaría
 * la consulta corriendo y las filas llegarían igual al navegador.
 */
export function AvisoMeetHoy() {
  const { puede, emailUsuario, esOwner } = usePermissions()
  const usuario = useMemo(() => ({ email: emailUsuario, esOwner }), [emailUsuario, esOwner])
  if (puede.verCalendario !== true) return null
  return <AvisoMeetHoyInterno usuario={usuario} />
}
